import { clsx } from 'clsx'
import { useRef, useState } from 'react'

import styles from 'components/chat-components/AgentMessage.module.css'
import { resolveMuxForVideoArtifact } from 'components/chat-components/resolveVideoArtifactMux'
import { applyAspectRatioFromEvent, applyPlaybackRateToMedia } from 'components/chat-components/videoArtifactMedia'
import { getNextPlaybackSpeed } from 'components/chat-components/videoPlaybackSpeed'
import { MuxPlaybackPlayer } from 'components/shared/MuxPlaybackPlayer'
import { Button } from 'components/ui/Button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from 'components/ui/DropdownMenu'
import { IconButton } from 'components/ui/IconButton'
import { webLogger } from 'utils/logger'

import type MuxPlayerElement from '@mux/mux-player'
import type { ContentRendererProperties, VideoArtifactContent } from 'components/chat-components/AgentMessage.types'
import type { ReactElement, SyntheticEvent } from 'react'

type VideoArtifactRendererProperties = {
  content: VideoArtifactContent
} & Pick<ContentRendererProperties, 'downloadFileName' | 'downloadVideoUrl' | 'mux' | 'videoReference' | 'videoSources'>

export const VideoArtifactRenderer = ({
  content,
  downloadFileName,
  downloadVideoUrl,
  mux,
  videoReference,
  videoSources,
}: VideoArtifactRendererProperties): ReactElement => {
  /* --- Section 1: Hooks --- */
  const containerReference = useRef<HTMLDivElement | null>(null)
  const muxPlayerReference = useRef<MuxPlayerElement | null>(null)
  const nativeVideoReference = useRef<HTMLVideoElement | null>(null)

  /* --- Section 2: Local State --- */
  const [isDownloading, setIsDownloading] = useState(false)
  const [playbackRate, setPlaybackRate] = useState(1)

  /* --- Section 3: Derived Values --- */
  const resolvedMux = resolveMuxForVideoArtifact(content.videoUrl, videoSources, mux)

  /* --- Section 4: Event Handlers --- */
  const handleNativeVideoReference = (element: HTMLVideoElement | null) => {
    nativeVideoReference.current = element
    if (videoReference) {
      videoReference.current = element
    }
  }

  const handleLoadedMetadata = (event: SyntheticEvent | Event) => {
    applyAspectRatioFromEvent(containerReference.current, event)
    applyPlaybackRateToMedia(muxPlayerReference.current, nativeVideoReference.current, playbackRate)
  }

  const handleCyclePlaybackRate = () => {
    setPlaybackRate(currentPlaybackRate => {
      const nextPlaybackRate = getNextPlaybackSpeed(currentPlaybackRate)
      applyPlaybackRateToMedia(muxPlayerReference.current, nativeVideoReference.current, nextPlaybackRate)
      return nextPlaybackRate
    })
  }

  const handleDownloadVideo = async () => {
    if (!downloadVideoUrl) {
      return
    }

    setIsDownloading(true)
    try {
      const response = await fetch(downloadVideoUrl, { mode: 'cors' })
      if (!response.ok) {
        throw new Error(`Download failed: ${response.status}`)
      }

      const blob = await response.blob()
      const objectUrl = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = objectUrl
      anchor.download = downloadFileName ?? 'video.mp4'
      anchor.rel = 'noopener'
      anchor.click()
      URL.revokeObjectURL(objectUrl)
    } catch (error: unknown) {
      webLogger.warn('Video download via fetch failed, falling back to opening URL', {
        error: error instanceof Error ? error.message : String(error),
      })
      window.open(downloadVideoUrl, '_blank', 'noopener,noreferrer')
    } finally {
      setIsDownloading(false)
    }
  }

  /* --- Section 6: Render --- */
  return (
    <div ref={containerReference} className={styles.contentVideoArtifact}>
      <div className={styles.videoArtifactChrome}>
        <Button
          aria-label={`Playback speed ${playbackRate}x`}
          className={styles.videoArtifactSpeedButton}
          onClick={handleCyclePlaybackRate}
          type="button"
          variant="plain"
        >
          {playbackRate}x
        </Button>
        {downloadVideoUrl ? (
          <div className={styles.videoArtifactMenuWrap}>
            <DropdownMenu positioning={{ placement: 'bottom-end' }}>
              <DropdownMenuTrigger asChild>
                <IconButton
                  className={styles.videoArtifactMoreButton}
                  disabled={isDownloading}
                  isLoading={isDownloading}
                  label="Video options"
                  size="sm"
                  variant="ghost"
                >
                  <span className={styles.videoArtifactMoreIcon} aria-hidden>
                    ⋯
                  </span>
                </IconButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={handleDownloadVideo} value="download-video">
                  Download video
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ) : undefined}
      </div>
      {resolvedMux ? (
        <MuxPlaybackPlayer
          ref={muxPlayerReference}
          className={clsx(styles.videoArtifactPlayer, styles.videoArtifactMuxPlayer)}
          playbackId={resolvedMux.playbackId}
          tokens={resolvedMux.tokens}
          streamType="on-demand"
          poster={resolvedMux.thumbnailUrl}
          onLoadedMetadata={handleLoadedMetadata}
        />
      ) : (
        <video
          key={content.videoUrl}
          ref={handleNativeVideoReference}
          className={styles.videoArtifactPlayer}
          src={content.videoUrl}
          controls
          preload="metadata"
          playsInline
          crossOrigin="anonymous"
          onLoadedMetadata={handleLoadedMetadata}
        >
          <track kind="captions" srcLang="en" label="English captions" src="data:text/vtt,WEBVTT" />
        </video>
      )}
    </div>
  )
}
